"use client";

import * as React from "react";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, Loader as Loader2, Mail, MapPin, Phone, Sparkles, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const projectTypeOptions = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const preferredTimingOptions = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const budgetRangeOptions = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
const trustBadges = [{
  icon: Clock,
  label: "Personal Follow-Up"
}, {
  icon: Sparkles,
  label: "Detailed Line-Item Proposals"
}];
function EstimateForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const validateZip = (value: string) => {
    if (!/^\d{5}$/.test(value.trim())) {
      setZipError("Please enter a valid 5-digit US ZIP code.");
      return false;
    }
    setZipError("");
    return true;
  };
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setZip(val);
    if (zipError && /^\d{5}$/.test(val.trim())) {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    const isZipValid = validateZip(zip);
    if (!consent) {
      setConsentError("Please agree to the communication terms to proceed.");
    } else {
      setConsentError("");
    }
    if (!isZipValid || !consent) {
      return;
    }
    setIsSubmitting(true);
    try {
      const currentPageUrl = typeof window !== "undefined" ? window.location.href : "";
      const payload = new URLSearchParams({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        project_type: projectType,
        address: zip.trim(),
        zip: zip.trim(),
        details: message.trim(),
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: currentPageUrl,
        form_location: "contact-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setSubmitError("We could not submit your request. Please call (470) 847-9076 or email serviceleadalerts@gmail.com.");
      }
    } catch {
      setSubmitError("A network error occurred. Please call (470) 847-9076 or email serviceleadalerts@gmail.com.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    setName("");
    setPhone("");
    setEmail("");
    setZip("");
    setProjectType("");
    setTiming("");
    setBudget("");
    setMessage("");
    setConsent(false);
    setZipError("");
    setConsentError("");
    setSubmitError(null);
    setIsSubmitted(false);
  };
  if (isSubmitted) {
    return <div className="flex flex-col items-center justify-center p-8 text-center sm:p-12">
        <div className="flex size-14 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="mt-4 font-serif text-2xl font-semibold text-foreground">
          Estimate request received
        </h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          Thank you for sharing your project vision. We will follow up to discuss
          your project and review your scope.
        </p>
        <div className="mt-6 flex flex-col gap-2 rounded-lg border border-border/80 bg-background/60 p-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-6">
          <a href="tel:4708479076" className="flex items-center gap-1.5 transition-colors hover:text-foreground">
            <Phone className="size-3.5 text-primary" />
            (470) 847-9076
          </a>
          <a href="mailto:serviceleadalerts@gmail.com" className="flex items-center gap-1.5 transition-colors hover:text-foreground">
            <Mail className="size-3.5 text-primary" />
            serviceleadalerts@gmail.com
          </a>
        </div>
        <Button onClick={handleReset} variant="outline" className="mt-6 border-border text-foreground hover:bg-accent hover:text-accent-foreground">
          Submit another request
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Full Name */}
        <div className="space-y-2">
          <label htmlFor="estimate-full-name" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            Full Name <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <User className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="estimate-full-name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" className="h-11 rounded-lg border-input bg-card pl-10 pr-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/40" />
          </div>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <label htmlFor="estimate-phone" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            Phone Number <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="estimate-phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(470) 847-9076" className="h-11 rounded-lg border-input bg-card pl-10 pr-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/40" />
          </div>
        </div>

        {/* Email Address */}
        <div className="space-y-2">
          <label htmlFor="estimate-email" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            Email Address <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="estimate-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" className="h-11 rounded-lg border-input bg-card pl-10 pr-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/40" />
          </div>
        </div>

        {/* ZIP Code */}
        <div className="space-y-2">
          <label htmlFor="estimate-zip" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            ZIP Code <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="estimate-zip" type="text" required value={zip} onChange={handleZipChange} onBlur={() => zip && validateZip(zip)} placeholder="30319" maxLength={5} className="h-11 rounded-lg border-input bg-card pl-10 pr-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/40" />
          </div>
          {zipError && <p className="text-xs text-destructive">{zipError}</p>}
        </div>
      </div>

      {/* Project Type (Required native HTML select with no preselection) */}
      <div className="space-y-2">
        <label htmlFor="estimate-project-type" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
          Project Type <span className="text-primary">*</span>
        </label>
        <select id="estimate-project-type" required value={projectType} onChange={e => setProjectType(e.target.value)} className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px]">
          <option value="" disabled>
            Select project type...
          </option>
          {projectTypeOptions.map((opt, i) => <option key={opt} value={opt} data-index={i}>
              {opt}
            </option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Preferred Timing (Optional select) */}
        <div className="space-y-2">
          <label htmlFor="estimate-timing" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            Preferred Timing{" "}
            <span className="text-[11px] font-normal normal-case text-muted-foreground">
              (Optional)
            </span>
          </label>
          <select id="estimate-timing" value={timing} onChange={e => setTiming(e.target.value)} className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px]">
            <option value="">Select timeframe...</option>
            {preferredTimingOptions.map((opt, i) => <option key={opt} value={opt} data-index={i}>
                {opt}
              </option>)}
          </select>
        </div>

        {/* Budget Range (Optional select) */}
        <div className="space-y-2">
          <label htmlFor="estimate-budget" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
            Budget Range{" "}
            <span className="text-[11px] font-normal normal-case text-muted-foreground">
              (Optional)
            </span>
          </label>
          <select id="estimate-budget" value={budget} onChange={e => setBudget(e.target.value)} className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px]">
            <option value="">Select budget range...</option>
            {budgetRangeOptions.map((opt, i) => <option key={opt} value={opt} data-index={i}>
                {opt}
              </option>)}
          </select>
        </div>
      </div>

      {/* Project Details / Message (Optional textarea) */}
      <div className="space-y-2">
        <label htmlFor="estimate-details" className="block text-xs font-semibold uppercase tracking-wider text-foreground">
          Project Details / Message{" "}
          <span className="text-[11px] font-normal normal-case text-muted-foreground">
            (Optional)
          </span>
        </label>
        <Textarea id="estimate-details" value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your remodeling project, scope, architectural style, or questions..." className="min-h-28 rounded-lg border-input bg-card px-3.5 py-3 text-sm text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/40" />
      </div>

      {/* Required Consent Checkbox */}
      <div className="space-y-1.5 pt-1">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input type="checkbox" required checked={consent} onChange={e => {
          setConsent(e.target.checked);
          if (e.target.checked) setConsentError("");
        }} className="mt-1 size-4 rounded-md border-input text-primary focus:ring-ring" />
          <span className="text-xs leading-relaxed text-muted-foreground">
            I agree to be contacted about my project by phone, text, or email.{" "}
            <span className="text-primary">*</span>
          </span>
        </label>
        {consentError && <p className="text-xs text-destructive">{consentError}</p>}
      </div>

      {/* Inline Submit Error */}
      {submitError && <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      {/* Submit Button */}
      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-lg border border-primary/80 bg-primary px-7 text-sm font-semibold tracking-wide text-primary-foreground shadow-md transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90 hover:shadow-lg disabled:opacity-50">
          {isSubmitting ? <span className="flex items-center justify-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              Submitting estimate request...
            </span> : "Submit Estimate Request"}
        </Button>
      </div>

      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        We respect your privacy. No hard sales or spam calls — just an honest,
        detailed review from our Brookhaven remodeling specialists.
      </p>
    </form>;
}
export default function Estimate() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="request-an-estimate" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-24 isolate">
      {/* Subtle architectural background pinstripe motif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_right,hsl(215_20%_88%/0.25)_1px,transparent_1px),linear-gradient(to_bottom,hsl(215_20%_88%/0.25)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,#000_70%,transparent_100%)]" />

      <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="outline" className="rounded-lg border-primary/35 bg-primary/10 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-foreground">
            Direct Planning Consultation
          </Badge>

          <h1 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-[40px]">
            Request your free estimate
          </h1>

          <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Tell us about your home and your plans — we will review the details
            and reach out to discuss your project.
          </p>

          {/* Architectural Gold Hairline */}
          <div aria-hidden="true" className="mx-auto mt-6 h-[1px] w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </div>

        {/* Form Container (Atelier Glass Card) */}
        <motion.div id="estimaterequestform" initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-10 rounded-2xl border border-border/80 bg-card/85 p-6 shadow-xl backdrop-blur-md sm:p-10">
          <EstimateForm />
        </motion.div>

        {/* Reassurance Trust Strip */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {trustBadges.map((badge, i) => {
          const Icon = badge.icon;
          return <div key={badge.label} data-index={i} className="flex items-center justify-center gap-2 rounded-lg border border-border/70 bg-card/60 px-4 py-3 text-xs font-medium text-foreground shadow-2xs backdrop-blur-xs">
                <Icon className="size-4 text-primary shrink-0" />
                <span>{badge.label}</span>
              </div>;
        })}
        </div>
      </div>
    </section>;
}